import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { FeatureAccessor } from '../featureAccessor';
import { Account } from './account';
import { BackToMenu } from './back';
import { Basket } from './basket';
import { CheckoutLocationIndicator } from './checkout-location-indicator';
import * as Styled from './styles';
import { AccountDropdown } from '@/components/account/accountDropdown';
import { AndroidBackButton } from '@/components/android/back-button';
import AndroidNavigation from '@/components/android/navigation-drawer';
import { styled } from '@/components/content/stitches';
import { GenericRouteAccessor, getAllowedRoutes } from '@/components/genericRouteAccessor';
import { Navigation } from '@/components/header/navigation-drawer';
import { Box, Stack } from '@/components/ui';
import { Render } from '@/components/uielements/render';
import { CSS_CLASSES } from '@/constants/cssClassNames';
import { FEATURE_FLAGS } from '@/constants/features';
import { ROUTES } from '@/constants/routes';
import { getDeliveryLine } from '@/features/menu-availability/destination';
import { MenuAvailability } from '@/features/menu-availability/menu-availability';
import { useSelector } from '@/redux';
import { selectConveyance } from '@/redux/conveyanceMode/reducer';
import { useConfigOverride } from '@/redux/cmsConfig';
import { getFontStyles } from '@/redux/cmsConfig/utils';
import { isAndroidShell } from '@/utils/android';
import { resizeImage } from '@/utils/imageHelper';
import { parseCustomUserAgent, getMobileStyles } from '@/utils/mobile';
import { isBrandDomain } from '@/utils/urls';
import { useOrganization } from '@/features/configs/organization';

export function Header() {
  const {
    background_color,
    background_image,
    border_color,
    font,
    logo,
    navigation_display,
    logo_cta_url,
    mobile_logo_width,
    desktop_logo_width,
  } = useConfigOverride('header');

  const router = useRouter();

  const me = useSelector((state) => state.app.me.data);
  const { organization } = useSelector((state) => state.app.organization);
  const { data: organizationConfig } = useOrganization();
  const isSingleLocation = organizationConfig?.locations?.features?.isSingleLocation;
  const webConfig = useSelector((state) => state.app.cmsConfig.webConfig);

  const logoImg = logo ? (
    <Logo
      css={{
        maxWidth: mobile_logo_width ? `${mobile_logo_width}px` : '$40',

        '@md': {
          maxWidth: desktop_logo_width ? `${desktop_logo_width}px` : '$48',
        },
      }}
      src={resizeImage(logo, { width: 300 })}
      alt={organization.label}
    />
  ) : (
    <Styled.OrganizationLabel theme={webConfig}>{organization.label}</Styled.OrganizationLabel>
  );

  const onAndroidSplashScreen = router.pathname.includes(ROUTES.ANDROID);
  const hasNavigation = Boolean(navigation_display !== 'hidden');
  const inlineMenu = Boolean(navigation_display === 'link list');

  const showMenuFiltering = getAllowedRoutes(router.pathname, [
    ROUTES.STORE,
    ROUTES.LOGIN,
    ROUTES.SIGN_UP,
  ]);

  // Below 768px the menu-availability row stacks the order-type toggle, the location
  // pill and, for a delivery order with an address, a "to <address>" line (TEAM-13315).
  // Only with a location is there a pill; the Android shell never mounts the row at all.
  // `isAndroidShell()` is client-only, so it is read after mount (docs/SSR_AND_HYDRATION.md rule 3).
  const { location, type: conveyanceType, address } = useSelector(selectConveyance);
  const [inAndroidShell, setInAndroidShell] = useState(false);
  useEffect(() => {
    setInAndroidShell(isAndroidShell());
  }, []);
  let mobileMenuRowHeight = '4rem';
  if (location && !inAndroidShell) {
    mobileMenuRowHeight = getDeliveryLine(conveyanceType, address) ? '6.5rem' : '5.5rem';
  }

  const isCOOMobile_iOS = parseCustomUserAgent()?.mobile_ios;

  const getLogoRoute = () => {
    // if we're using a vanity domain, redirect to the brand's marketing site
    if (isBrandDomain()) {
      return `https://${window.location.hostname}`;
    }
    if (isSingleLocation?.enabled && isSingleLocation.storeLocationId) {
      return `/store/${isSingleLocation.storeLocationId}`;
    }
    // return the logo_cta_url if it exists, otherwise default to the stores page
    return !!logo_cta_url ? logo_cta_url : ROUTES.STORES;
  };

  return (
    <>
      {isAndroidShell() && onAndroidSplashScreen && <AndroidNavigation />}

      {!onAndroidSplashScreen && (
        <Container
          data-css-override="StyledHeader"
          /**
           * Do not interpolate a client-only value here. `isCOOMobile_iOS` is
           * `undefined` server-side and `false` on the client, so this className
           * used to diverge on every page. The mobile height override lives in
           * the `css` prop below.
           * @see docs/SSR_AND_HYDRATION.md
           */
          className={CSS_CLASSES.GLOBAL_HEADER.CONTAINER}
          css={{
            backgroundColor: background_color ?? 'white',
            backgroundImage: background_image ? `url(${background_image})` : 'none',
            borderBottom: `1px solid ${border_color ?? 'transparent'}`,
            ...getFontStyles(font),
            ...(isCOOMobile_iOS && {
              ...getMobileStyles(['paddingTop']),
              height: 'auto',
              paddingBottom: '12px',
            }),
            '@xl': {
              marginBottom: 0,
            },
            ...(showMenuFiltering && {
              '@media (max-width: 767px)': {
                // Reserve the menu-availability row (its height less the 0.5rem it always overlapped).
                marginBottom: `calc(${mobileMenuRowHeight} - 0.5rem)`,
              },
            }),
          }}
          showMenuFiltering={showMenuFiltering}
        >
          {isAndroidShell() ? (
            <AndroidBackButton />
          ) : (
            <>
              <Box
                css={{
                  alignItems: 'center',
                  display: 'grid',
                  gridTemplateColumns: '$16 1fr $16',
                  gridTemplateRows: '1fr',

                  '@md': {
                    display: 'flex',
                    overflow: 'hidden',
                    gap: '$10',
                  },
                }}
              >
                <Navigation
                  hasNavigation={hasNavigation}
                  inlineMenu={inlineMenu}
                  signedIn={Boolean(me?.id)}
                />

                <div>
                  <FeatureAccessor
                    featureFlag={FEATURE_FLAGS.GLOBAL__LOCATIONS_PAGE}
                    renderFallback={logoImg}
                  >
                    <Link href={getLogoRoute()} passHref={true} legacyBehavior>
                      <LogoLink
                        data-testid="nav-logo-link"
                        id="nav-logo"
                        target={Boolean(logo_cta_url) ? '_blank' : '_self'}
                        css={{
                          order: inlineMenu ? 1 : 2,
                        }}
                      >
                        {logoImg}
                      </LogoLink>
                    </Link>
                  </FeatureAccessor>
                </div>

                {/* Do not show this on the checkout page */}
                {router.pathname !== ROUTES.CHECKOUT &&
                  router.pathname !== ROUTES.ORDER_CONFIRMATION &&
                  router.pathname !== ROUTES.ARRIVAL_CONFIRMATION && <Basket showOnMobile={true} />}

                {!hasNavigation && (
                  <GenericRouteAccessor
                    allowedPages={{
                      [ROUTES.ACCOUNT]: null,
                      [ROUTES.CHECKOUT]: { exactMatch: true },
                      [ROUTES.LOGIN]: { exactMatch: true },
                      [ROUTES.SIGN_UP]: { exactMatch: true },
                    }}
                    component={
                      <Box
                        css={{
                          display: 'none',
                          order: 3,

                          '@md': {
                            display: 'block',
                          },
                        }}
                      >
                        <BackToMenu />
                      </Box>
                    }
                  />
                )}
              </Box>

              <Render condition={showMenuFiltering}>
                <Stack
                  css={{
                    backgroundColor: background_color ?? 'white',
                    borderBottom: `1px solid ${border_color ?? 'transparent'}`,
                    height: '$14',
                    left: 0,
                    padding: '0 $4',
                    position: 'absolute',
                    top: 'calc(100% + 1px)',
                    width: '100%',

                    '@md': {
                      padding: '0 $10',
                    },

                    '@xl': {
                      backgroundColor: 'revert',
                      border: 'revert',
                      minWidth: 0,
                      padding: '0 $2',
                      position: 'static',
                      width: 'revert',
                    },

                    '@media (max-width: 767px)': {
                      height: mobileMenuRowHeight,
                    },
                  }}
                  position="center"
                >
                  <MenuAvailability />
                </Stack>
              </Render>

              <Stack
                css={{
                  display: 'none',
                  gap: '$4',

                  '@md': {
                    display: 'flex',
                  },
                }}
              >
                <Box
                  css={{
                    '@md': {
                      marginLeft: 'auto',
                    },
                  }}
                >
                  <Account />
                </Box>

                {/* Do not show this on the checkout page */}
                {router.pathname !== ROUTES.CHECKOUT &&
                  router.pathname !== ROUTES.ORDER_CONFIRMATION &&
                  router.pathname !== ROUTES.ARRIVAL_CONFIRMATION && (
                    <Basket showOnMobile={false} />
                  )}
              </Stack>
            </>
          )}
        </Container>
      )}

      <AccountDropdown />
      <CheckoutLocationIndicator />
    </>
  );
}

const Container = styled('header', {
  alignItems: 'center',
  display: 'grid',
  gridTemplateColumns: '1fr',
  height: '$12',
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 11,

  '@md': {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(min-content, 1fr))',
    height: '$20',
    padding: '0 $10',
  },

  variants: {
    showMenuFiltering: {
      false: {
        '@xl': {
          gridTemplateColumns: '1fr 1fr',
        },
      },
      true: {
        marginBottom: '$14', // used to push other content out of the way for menu availability

        '@xl': {
          gridTemplateColumns: '16rem minmax(0, 1fr) 16rem',
          marginBottom: 0,
        },
      },
    },
  },
  defaultVariants: {
    showMenuFiltering: false,
  },
});

const LogoLink = styled('a', {
  display: 'flex',
  cursor: 'pointer',
  justifyContent: 'center',

  '@md': {
    justifyContent: 'flex-start',
  },
});

const Logo = styled('img', {
  display: 'block',
  height: '$10',
  objectFit: 'contain',
  objectPosition: 'center',

  '@md': {
    height: '$14',
    objectPosition: 'left',
  },
});
